import React, { useState, useEffect } from 'react';
import { LandingPageRenderer } from './LandingPageRenderer';
import { ErrorPage } from './ErrorPage';
import campaignService from '../../services/CampaignService';

// Function to validate campaign data structure
const validateCampaignData = (campaign) => {
  if (!campaign) return false;
  
  const requiredFields = ['id', 'name', 'status'];
  const hasRequiredFields = requiredFields.every(field => campaign[field] !== undefined && campaign[field] !== null);
  
  if (!hasRequiredFields) {
    console.error('Campaign missing required fields:', {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      hasAllRequired: hasRequiredFields
    });
    return false;
  }
  
  if (campaign.status !== 'online') {
    console.error('Campaign is not online:', campaign.status);
    return false;
  }
  
  return true;
};

// Function to get campaign by URL from PocketBase
const getCampaignByUrl = async (companyName, campaignName, landingPageName) => {
  try {
    // First try the direct route lookup
    const routeResult = await campaignService.getCampaignByRoute(companyName, campaignName, landingPageName);
    
    if (routeResult.success) {
      // Convert PocketBase data to our format
      const campaignData = {
        id: routeResult.data.id,
        name: routeResult.data.name,
        applicantCount: (routeResult.data.applicants || []).length,
        color: routeResult.data.color,
        status: routeResult.data.status,
        landingPage: routeResult.data.landing_page_data,
        questionnaire_data: routeResult.data.questionnaire_data,
        applicants: routeResult.data.applicants || []
      };
      
      // Validate campaign data
      if (validateCampaignData(campaignData)) {
        return campaignData;
      }
    }
    
    // Fallback: get all campaigns and find by route data
    const allCampaignsResult = await campaignService.getCampaigns();
    
    if (allCampaignsResult.success) {
      const campaign = allCampaignsResult.data.find(c => 
        c.company_name === companyName && 
        c.campaign_name === campaignName && 
        c.landing_page_name === landingPageName
      );
      
      if (campaign) {
        const campaignData = {
          id: campaign.id,
          name: campaign.name,
          applicantCount: (campaign.applicants || []).length,
          color: campaign.color,
          status: campaign.status,
          landingPage: campaign.landing_page_data,
          questionnaire_data: campaign.questionnaire_data,
          applicants: campaign.applicants || []
        };
        
        // Validate campaign data
        if (validateCampaignData(campaignData)) {
          return campaignData;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    return null;
  }
};

export function PublicLandingPage({ match }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set document title
  useEffect(() => {
    // Check if we're on karrieresichern.de domain
    if (window.location.hostname === 'karrieresichern.de') {
      document.title = 'Karrieresichern';
    } else if (match?.params?.companyName && match?.params?.campaignName) {
      document.title = `${match.params.companyName} - ${match.params.campaignName}`;
    } else {
      document.title = 'Landing Page';
    }
  }, [match?.params?.companyName, match?.params?.campaignName]);

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setLoading(true);
        const { companyName, campaignName, landingPageName } = match.params;
        const campaignData = await getCampaignByUrl(companyName, campaignName, landingPageName);
        
        if (campaignData) {
          // Debug: Log campaign data to help identify issues
          console.log('Landing page campaign data loaded:', {
            id: campaignData?.id,
            name: campaignData?.name,
            status: campaignData?.status,
            hasLandingPageData: !!campaignData?.landingPage,
            hasQuestionnaireData: !!campaignData?.questionnaire_data,
            applicantCount: campaignData?.applicantCount || 0
          });
          
          // Check if campaign is offline
          if (campaignData.status === 'offline') {
            console.error('Landing page campaign is offline:', campaignData.id);
            setError('Kampagne ist offline');
          } else {
            setCampaign(campaignData);
          }
        } else {
          console.error('Landing page campaign not found for route:', match.params);
          setError('Kampagne nicht gefunden');
        }
      } catch (err) {
        setError('Fehler beim Laden der Kampagne');
        console.error('Error loading campaign:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, [match.params]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center font-sf-pro">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-300">Lade Landing Page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Show 404 page for offline campaigns or not found campaigns
    if (error === 'Kampagne ist offline' || error === 'Kampagne nicht gefunden') {
      return (
        <ErrorPage
          title="Kampagne nicht verfügbar"
          message={error === 'Kampagne ist offline' ? 'Diese Kampagne ist derzeit offline und nicht verfügbar.' : 'Diese Kampagne wurde nicht gefunden.'}
          subtitle="Mögliche Gründe: Die Kampagne wurde beendet, ist offline oder der Link ist nicht mehr gültig."
          errorCode="404"
          errorType="Not Found"
          showBackButton={true}
        />
      );
    }
    
    // Show generic error for other issues
    return (
      <ErrorPage
        title="Fehler"
        message={error}
        subtitle="Bitte versuchen Sie es später erneut."
        errorCode="500"
        errorType="Internal Server Error"
        showBackButton={true}
      />
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center font-sf-pro">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Kampagne nicht gefunden</h1>
          <p className="text-slate-600 dark:text-slate-300">Die angeforderte Kampagne existiert nicht.</p>
        </div>
      </div>
    );
  }

  // Check if campaign is offline
  if (campaign.status === 'offline') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center font-sf-pro">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Kampagne nicht verfügbar</h1>
          <p className="text-slate-600 dark:text-slate-300">Diese Kampagne ist derzeit offline und nicht verfügbar.</p>
        </div>
      </div>
    );
  }

  return <LandingPageRenderer campaign={campaign} />;
}
